import React, { useState, useEffect } from 'react';
import { getBrands, deleteBrand } from '../services/api';
import BrandForm from './BrandForm';
import ProductList from './ProductList';
import PageHeader from './PageHeader';
import './BrandList.css';

function BrandList({ user, canEdit = false }) {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal/View states
  const [showForm, setShowForm] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null); // For viewing products
  const [actionLoading, setActionLoading] = useState(false);

  // Fetch brands
  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await getBrands(searchTerm);
      if (response.success) {
        setBrands(response.brands);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchBrands();
  };

  // Handle add new brand
  const handleAddNew = () => {
    setEditingBrand(null);
    setShowForm(true);
  };

  // Handle edit brand
  const handleEdit = (brand, e) => {
    e.stopPropagation();
    setEditingBrand(brand);
    setShowForm(true);
  };

  // Handle delete brand
  const handleDelete = async (brandId) => {
    try {
      setActionLoading(true);
      const response = await deleteBrand(brandId);
      if (response.success) {
        setDeleteConfirm(null);
        fetchBrands();
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle form success
  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingBrand(null);
    fetchBrands();
  };

  // Handle brand click to view products
  const handleBrandClick = (brand) => {
    setSelectedBrand(brand);
  };

  // If a brand is selected, show products
  if (selectedBrand) {
    return (
      <ProductList 
        brand={selectedBrand} 
        user={user}
        canEdit={canEdit}
        onBack={() => setSelectedBrand(null)}
      />
    );
  }

  return (
    <div className="brand-list-container">
      <PageHeader
        className="brand-list-header"
        title="Brands"
        subtitle="Manage product brands and catalogs"
        actions={canEdit && (
          <button className="add-btn" onClick={handleAddNew}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add Brand
          </button>
        )}
      />

      {/* Search */}
      <form onSubmit={handleSearch} className="search-section">
        <div className="search-box">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            type="text"
            placeholder="Search brands..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button type="submit" className="search-btn">Search</button>
      </form>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading brands...</p>
        </div>
      ) : brands.length === 0 ? (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <circle cx="8.5" cy="8.5" r="1.5"></circle>
            <polyline points="21 15 16 10 5 21"></polyline>
          </svg>
          <h3>No brands found</h3>
          <p>{searchTerm ? 'Try a different search term' : 'Get started by adding your first brand'}</p>
          {canEdit && !searchTerm && (
            <button onClick={handleAddNew}>Add Brand</button>
          )}
        </div>
      ) : (
        /* Brands Grid */
        <div className="brands-grid">
          {brands.map((brand) => (
            <div 
              key={brand.id} 
              className="brand-card"
              onClick={() => handleBrandClick(brand)}
            >
              <div className="brand-logo">
                {brand.logo ? (
                  <img 
                    src={brand.logo} 
                    alt={brand.name}
                    onError={(e) => {
                      console.error('Failed to load image:', brand.logo);
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className="logo-placeholder"
                  style={{ display: brand.logo ? 'none' : 'flex' }}
                >
                  {brand.name.charAt(0).toUpperCase()}
                </div>
              </div>
              <div className="brand-info">
                <h3>{brand.name}</h3>
                {brand.website && (
                  <a 
                    href={brand.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="brand-website"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="2" y1="12" x2="22" y2="12"></line>
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                    </svg>
                    Website
                  </a>
                )}
              </div>
              <div className="brand-actions">
                <span className="view-products">View Products →</span>
                {canEdit && (
                  <div className="action-buttons">
                    <button 
                      className="edit-btn" 
                      onClick={(e) => handleEdit(brand, e)}
                      title="Edit Brand"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <button 
                      className="delete-btn" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm(brand);
                      }}
                      title="Delete Brand"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Total Count */}
      {!loading && brands.length > 0 && (
        <div className="list-footer">
          <span>Total: {brands.length} brands</span>
        </div>
      )}

      {/* Brand Form Modal */}
      {showForm && (
        <BrandForm
          brand={editingBrand}
          onSuccess={handleFormSuccess}
          onClose={() => {
            setShowForm(false);
            setEditingBrand(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay">
          <div className="confirm-modal">
            <div className="confirm-icon delete">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
            </div>
            <h3>Delete Brand</h3>
            <p>Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?</p>
            <p className="warning">This will also delete all products under this brand. This action cannot be undone.</p>
            <div className="modal-actions">
              <button 
                className="cancel-btn" 
                onClick={() => setDeleteConfirm(null)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn" 
                onClick={() => handleDelete(deleteConfirm.id)}
                disabled={actionLoading}
              >
                {actionLoading ? 'Deleting...' : 'Delete Brand'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BrandList;
